/**
 * Codex (§2) — per-chakra kennisbank. Een chakra-intro is toegankelijk zodra
 * dat chakra ontgrendeld is; vergrendelde chakra's worden grijs getoond.
 */
import { useEffect, useState } from 'react';
import type { Origin } from '../data/types';
import { CHAKRA_VOLGORDE } from '../data/chakras';
import { useProgressStore, isOntgrendeld } from '../store/progressStore';
import { codexTekst, wereldNaam, elementNaam, resourceNaam } from '../data/content';
import { t, type Taal } from '../i18n';

interface Props {
  taal: Taal;
  onTerug: () => void;
}

export function Codex({ taal, onTerug }: Props) {
  const voltooid = useProgressStore((s) => s.voltooideChakras);
  const markeerGezien = useProgressStore((s) => s.markeerGezien);
  const [open, setOpen] = useState<Origin | null>(null);

  useEffect(() => {
    if (open !== null) markeerGezien(`codex-${open}`);
  }, [open, markeerGezien]);

  if (open !== null) {
    const c = CHAKRA_VOLGORDE.find((x) => x.header.nummer === open)!;
    const h = c.header;
    const feiten: [string, string][] = [
      ['Sanskriet', h.sanskriet],
      ['Element', elementNaam(h.element, taal)],
      ['Mantra', `${h.mantra} (${h.freq}Hz)`],
      [t('ui.resource', taal), resourceNaam(h.resourceLabel, taal)],
      ['Bloemblaadjes', String(h.nummer === 7 ? '±1000' : h.bloemblaadjes)],
      [t('ui.wereld', taal), wereldNaam(c, taal)],
    ];
    return (
      <div className="flex h-full w-full flex-col bg-achtergrond text-slate-100" style={{ background: `radial-gradient(circle at 50% 0%, ${h.kleur}22, #0a0a0f 60%)` }}>
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-2">
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Cinzel, serif', color: h.accent }}>{h.naam} <span className="text-sm text-slate-400">{h.sanskriet}</span></h2>
          <button type="button" onClick={() => setOpen(null)} className="rounded border border-white/15 px-3 py-1 text-sm hover:bg-white/5">← {t('ui.terug', taal)}</button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="mx-auto max-w-2xl">
            <div className="mb-4 flex flex-wrap gap-2">
              {feiten.map(([k, v]) => (
                <span key={k} className="rounded-full border px-3 py-1 text-xs" style={{ borderColor: `${h.accent}55`, color: '#ece6da' }}>
                  <span className="text-slate-400">{k}:</span> {v}
                </span>
              ))}
            </div>
            <p className="text-base leading-relaxed text-slate-200" style={{ fontFamily: 'EB Garamond, serif' }}>{codexTekst(c, taal)}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mandala-textuur flex h-full w-full flex-col bg-achtergrond text-slate-100">
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-2">
        <h2 className="text-lg font-bold" style={{ fontFamily: 'Cinzel, serif', color: '#c9a86a' }}>{t('ui.codex', taal)}</h2>
        <button type="button" onClick={onTerug} className="rounded border border-white/15 px-3 py-1 text-sm hover:bg-white/5">← {t('ui.terug', taal)}</button>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        <ul className="mx-auto flex max-w-2xl flex-col gap-2">
          {CHAKRA_VOLGORDE.map((c) => {
            const h = c.header;
            const ontgrendeld = isOntgrendeld(h.nummer, voltooid);
            return (
              <li key={h.nummer}>
                <button
                  type="button"
                  disabled={!ontgrendeld}
                  onClick={() => setOpen(h.nummer)}
                  className={['flex w-full items-center gap-3 rounded-xl border px-4 py-2 text-left transition', ontgrendeld ? 'hover:-translate-y-0.5' : 'cursor-not-allowed opacity-40'].join(' ')}
                  style={{ borderColor: `${h.accent}55`, background: `linear-gradient(120deg, #14121c, ${h.kleur}22)` }}
                >
                  <span className="h-7 w-7 shrink-0 rounded-full" style={{ background: `radial-gradient(circle, ${h.accent}, ${h.kleur})` }} />
                  <span className="flex-1">
                    <span className="block font-semibold" style={{ fontFamily: 'Cinzel, serif', color: '#ece6da' }}>{h.naam}</span>
                    <span className="block text-[11px] text-slate-400">{elementNaam(h.element, taal)} · {resourceNaam(h.resourceLabel, taal)}</span>
                  </span>
                  {!ontgrendeld && <span>🔒</span>}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
